import React, { useCallback } from 'react';
import { DateUtils } from 'react-day-picker';
import { t } from '@lingui/macro';

import { CalendarDirection } from '@rover/components/src/datetime/DatePicker';
import DateRangePicker, {
  MobileDirection,
} from '@rover/components/src/datetime/DateRangePicker/DateRangePicker';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { SERVICE_SELECTION_OPTIONS } from '@rover/constants/src/translations/serviceSelector.constants';
import Rebook from '@rover/icons/svg/rebook.svg';
import { Box, Button, Heading } from '@rover/kibble/core';
import { DayTimeAvailability } from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Dates';
import { shouldShowControls } from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import FilterLabel from '@rover/shared/js/search/components/FilterLabel';
import OneTimeRepeatToggleButtons from '@rover/shared/js/search/components/OneTimeRepeatToggleButtons';
import RepeatServiceScheduler from '@rover/shared/js/search/components/RepeatServiceScheduler';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { AvailabilitySearchFilters } from '@rover/types/src/AvailabilitySearchFilters';
import type { UpdatedDateRange } from '@rover/types/src/datetime/DateRange';
import type { Day } from '@rover/types/src/datetime/Day';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';

import { SpacesRequired } from '../../Filters';
import { SearchDesktopSelectDates } from '../analyticsEvents';

type DatesDropdownContentProps = {
  searchFilters: SearchFilters;
  language: string;
  onChange: (filters: Partial<SearchFilters>) => void;
  onSave: () => void;
  serviceType: ServiceType;
  serviceTypeFilterOptions: SearchFiltersOptions;
};

const getDatePlaceholders = (service: ServiceType): { start: string; end: string } => {
  const isBoarding = service === ServiceType.OVERNIGHT_BOARDING;

  let datePlaceholders = { start: t`Start`, end: t`End` };
  if (isBoarding) {
    datePlaceholders = { start: t`Drop off`, end: t`Pick up` };
  }
  return datePlaceholders;
};

const DatesDropdownContent = ({
  searchFilters,
  language,
  onChange,
  onSave,
  serviceType,
  serviceTypeFilterOptions,
}: DatesDropdownContentProps): JSX.Element => {
  const { frequency, startDate, endDate, selectedDays } = searchFilters;

  const isOvernightService =
    serviceType === ServiceType.OVERNIGHT_BOARDING ||
    serviceType === ServiceType.OVERNIGHT_TRAVELING;

  const handleFrequencyChange = useCallback(
    (serviceFrequency?: ServiceFrequencyEnum) => {
      const shouldResetDate = serviceFrequency === ServiceFrequencyEnum.REPEAT;
      const shouldResetDays = serviceFrequency === ServiceFrequencyEnum.ONE_TIME;
      onChange({
        frequency: serviceFrequency,
        ...(shouldResetDate && { endDate: undefined }),
        ...(shouldResetDays && { selectedDays: [] }),
      });
    },
    [onChange]
  );

  const datePlaceholders = getDatePlaceholders(serviceType);

  const handleDateRangeChange = useCallback(
    (dateRange: UpdatedDateRange) => {
      onChange({ startDate: dateRange.startDate, endDate: dateRange.endDate });
      emitAnalyticsEvent(new SearchDesktopSelectDates());
    },
    [onChange]
  );

  const handleRepeatStartDateChange = useCallback(
    (selectedStartDate?: Date | null) => {
      const hasValidDates = endDate && selectedStartDate;
      const isStartAfterEnd = hasValidDates && DateUtils.isDayAfter(selectedStartDate, endDate);
      const updatedEndDate = hasValidDates && !isStartAfterEnd ? endDate : undefined;
      onChange({ startDate: selectedStartDate, endDate: updatedEndDate });
      emitAnalyticsEvent(new SearchDesktopSelectDates());
    },
    [endDate, onChange]
  );

  const handleSelectedDaysChange = (days: Day[]): void => {
    onChange({ selectedDays: days });
  };

  const handleAvailabilityChange = (availabilityFilters: AvailabilitySearchFilters) => {
    onChange(availabilityFilters);
  };

  const { morningAvailability, middayAvailability, eveningAvailability } = serviceTypeFilterOptions;

  const { showDaytimeAvailability, showSpacesRequired } = shouldShowControls({
    serviceTypeSlug: serviceType,
    serviceOptions: serviceTypeFilterOptions,
  });

  const selectedServiceType =
    SERVICE_SELECTION_OPTIONS().find((option) => option.value === serviceType)?.title || '';

  return (
    <Box width="100%" backgroundColor="white" borderRadius="primary">
      <Box p="6x" sx={{ textAlign: 'left' }}>
        <Heading size="300" textAlign="left" mb="4x">
          {t`When do you need ${selectedServiceType}?`}
        </Heading>

        {!isOvernightService && (
          <OneTimeRepeatToggleButtons
            displayAs="compact"
            frequency={frequency}
            label={t`How often?`}
            onChange={handleFrequencyChange}
            repeatIcon={Rebook}
          />
        )}

        {isOvernightService || frequency === ServiceFrequencyEnum.ONE_TIME ? (
          <Box>
            <FilterLabel>{t`Dates`}</FilterLabel>
            <DateRangePicker
              language={language}
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
              startPlaceholder={datePlaceholders.start}
              endPlaceholder={datePlaceholders.end}
              mobileDirection={MobileDirection.ROW}
              calendarDirection={CalendarDirection.DOWN}
            />
          </Box>
        ) : (
          <RepeatServiceScheduler
            language={language}
            onSelectedDaysChange={handleSelectedDaysChange}
            onStartDateChange={handleRepeatStartDateChange}
            repeatStartDateLabel={t`Start Date`}
            selectedDays={selectedDays || []}
            showLabel
            serviceName={selectedServiceType}
            startDate={startDate}
            startPlaceholder={datePlaceholders.start}
            disableValidation
          />
        )}

        {showDaytimeAvailability && (
          <Box mt="4x">
            <DayTimeAvailability
              availabilityBuckets={{
                morning: morningAvailability?.label ?? null,
                midday: middayAvailability?.label ?? null,
                evening: eveningAvailability?.label ?? null,
              }}
              label={t`Which time(s) do you need?`}
              labelIsVisible
              onChange={handleAvailabilityChange}
              searchFilters={searchFilters}
            />
          </Box>
        )}

        {showSpacesRequired && (
          <Box mt="4x">
            <SpacesRequired
              searchFilters={searchFilters}
              onChange={(spacesRequired) => onChange({ spacesRequired })}
            />
          </Box>
        )}

        <Box mt="6x">
          <Button variant="primary" fullWidth size="small" onPress={onSave}>
            {t`Save`}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DatesDropdownContent;
